import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./HotelManagment.module.css";
import { Search, Download, RefreshCw, ExternalLink, MapPin, Star, KeyRound, Phone, Mail, ImageOff, Filter, ChevronDown, FileText, Layers, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Map, Globe } from "lucide-react";
import { flagSrcForCode } from "../../../../utils/countryFlags";
import AddHotelModal from "../../../../components/Products/Hotels/MasterData/AddHotelModal";
import useDebouncedCallback from "../../../../hooks/useDebouncedCallback";
import {
  createHotel,
  getHotelCities,
  getHotelCountries,
  getHotelZones,
  getHotels,
  refreshHotelContent,
  getRatingSwitchOffPreview,
  applyRatingSwitchOff,
  mapApiHotelToUi,
  mapUiCreateHotelToApi,
  toggleHotelStatus,
  uploadHotelImage,
  useGetHotels,
} from "../../../../api/products/useHotelsApi";
import { useToast } from "../../../../context/ToastContext";
import { hotelsToCsv } from "./hotelCsv";
import { hotelsToPrintableHtml } from "./hotelPdf";

// ── Export helpers ────────────────────────────────────────────────────────────
const downloadCsv = (csv, filename) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Render a printable HTML report into a hidden iframe and open the print dialog, where the user
// picks "Save as PDF". srcdoc guarantees onload fires, so we print only once the document is
// laid out; the iframe is removed after printing (with a safety timeout in case the user
// cancels and no afterprint event arrives).
const printHtml = (html) => {
  const iframe = document.createElement("iframe");
  Object.assign(iframe.style, { position: "fixed", right: 0, bottom: 0, width: 0, height: 0, border: 0 });
  iframe.setAttribute("aria-hidden", "true");
  iframe.onload = () => {
    const win = iframe.contentWindow;
    const remove = () => { if (iframe.parentNode) iframe.remove(); };
    win.onafterprint = remove;
    win.focus();
    win.print();
    setTimeout(remove, 60000);
  };
  iframe.srcdoc = html;
  document.body.appendChild(iframe);
};

// Rows fetched per request when exporting ALL pages. Larger than the on-screen page so a big
// export is a handful of requests, not hundreds; small enough that each response stays quick.
const EXPORT_BATCH = 500;
// Hard ceiling so a mis-click on an unfiltered 67k-row table can't fire ~140 requests. Above
// this the user is asked to narrow with a filter first.
const EXPORT_MAX = 20000;

// How many hotels a non-geo filter may refresh in one go. A search that lands on a single hotel
// is the point of it; a broad filter matching thousands belongs to a geo scope, which the
// backend batches instead. Kept in step with the server's own ceiling.
const REFRESH_FILTER_MAX = 200;

// Field groups offered in the "Refresh content" dialog. Each maps to a `fields` flag the
// backend gates its writes on, so a hand-curated value is only overwritten when explicitly
// ticked. Order = safest (pure Hotelbeds content) first, hand-curated last.
const REFRESH_OPTIONS = [
  { key: "basic",       label: "Name & category",       hint: "Hotel name, star category, chain, accommodation type" },
  { key: "description", label: "Description text",       hint: "The main hotel description (English)" },
  { key: "facilities",  label: "Facilities & amenities", hint: "Replaces the facilities list with Hotelbeds'" },
  { key: "images",      label: "Add new photos",         hint: "Adds Hotelbeds photos you don't already have — uploads & master photo are always kept" },
  { key: "zone",        label: "Zone",                   hint: "Overwrites a zone you may have set by hand" },
  { key: "location",    label: "Map location & address", hint: "Latitude / longitude, address, postal code" },
  { key: "geo",         label: "Country / region codes", hint: "Country, region and destination codes" },
];

// Official-class filter values, shared with the API. Hotels are rated in STARS and
// apartments in KEYS, and a large slice of the catalogue carries neither.
const categoryFilterLabel = (value) => {
  if (!value || value === "all") return "";
  if (value === "none") return "Without official class";
  if (value === "stars") return "Any star hotel";
  if (value === "keys") return "Any key apartment";
  const m = /^(star|key):([1-5])$/.exec(value);
  if (!m) return value;
  return m[1] === "key" ? `${m[2]}-key apartment` : `${m[2]}★`;
};

// Hotels shown per page: the "per page" choice in the pagination bar (Levent, 8 Oct 2026). Row
// photos load lazily, so 100 rows only fetch the thumbnails that scroll into view. The choice is
// remembered in this browser.
const PAGE_SIZE_OPTIONS = [25, 50, 75, 100];
const PAGE_SIZE = PAGE_SIZE_OPTIONS[0];
const PAGE_SIZE_KEY = "sunsky.admin.hotels.perPage";
const readPageSize = () => {
  try {
    const n = Number(window.localStorage.getItem(PAGE_SIZE_KEY));
    return PAGE_SIZE_OPTIONS.includes(n) ? n : PAGE_SIZE;
  } catch { return PAGE_SIZE; }
};

// Hover-preview geometry. The delay stops the card flashing while the pointer merely
// sweeps down the column; the height is the card's fixed layout (image + caption + padding),
// which lets us centre it on the thumbnail before the large image has decoded.
const PREVIEW_DELAY = 130;
const PREVIEW_FADE = 200;
const PREVIEW_W = 340;
const PREVIEW_H = 258;
const PREVIEW_GAP = 14;

// Placeholder shown when a hotel has no master image, or its URL is dead. Also the
// visual the preview card falls back to, so a broken image never reaches the user.
const ThumbFallback = ({ size = 26, title = "No master image" }) => (
  <div className={styles.hotelIcon} title={title}>
    <ImageOff size={size} />
  </div>
);

// Shown in CATEGORY for a property with no official classification. A building with a query
// badge — the building's lower-right outline stops short of the badge rather than running
// under it. Inline (not an <img>) so it inherits currentColor and stays crisp at any size.
const NoCategoryIcon = ({ size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {/* canopy, walls and ground — the right wall and ground line are cut where the badge sits */}
    <rect x="3.2" y="3" width="19" height="3.8" rx="1.4" />
    <path d="M5.5 6.8V25.5" />
    <path d="M19.9 6.8V17" />
    <path d="M3.4 25.5h13.4" />
    {/* three rows of windows */}
    <rect x="7.6" y="9.4" width="2.8" height="2.8" rx="0.7" />
    <rect x="11.4" y="9.4" width="2.8" height="2.8" rx="0.7" />
    <rect x="15.2" y="9.4" width="2.8" height="2.8" rx="0.7" />
    <rect x="7.6" y="13.6" width="2.8" height="2.8" rx="0.7" />
    <rect x="11.4" y="13.6" width="2.8" height="2.8" rx="0.7" />
    <rect x="15.2" y="13.6" width="2.8" height="2.8" rx="0.7" />
    <rect x="7.6" y="17.8" width="2.8" height="2.8" rx="0.7" />
    <rect x="11.4" y="17.8" width="2.8" height="2.8" rx="0.7" />
    {/* door */}
    <path d="M10.4 25.5v-3.6a1.4 1.4 0 0 1 1.4-1.4h2.2a1.4 1.4 0 0 1 1.4 1.4v3.6" />
    {/* query badge */}
    <circle cx="24" cy="23.5" r="6.4" />
    <path d="M22.1 21.5a2 2 0 0 1 3.8.7c0 1.3-1.9 1.7-1.9 2.9" />
    <path d="M24 27.6h.01" />
  </svg>
);

// Master image thumbnail. Shows a shimmer skeleton until the image decodes, the image
// itself once it does, and a branded placeholder if it errors. Hovering an image opens a
// large preview in a body portal — the table container is overflow:hidden, so an in-flow
// popup would be clipped by it.
const HotelThumb = ({ src, alt }) => {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [anchor, setAnchor] = useState(null); // {top,left,toLeft} — set once positioned
  const [shown, setShown] = useState(false);  // drives the fade/scale transition
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const openTimer = useRef(null);
  const revealTimer = useRef(null);
  const closeTimer = useRef(null);

  const clearTimers = () => {
    clearTimeout(openTimer.current);
    clearTimeout(revealTimer.current);
    clearTimeout(closeTimer.current);
  };

  // Reset per hotel (rows are recycled across pages). A cached image can finish decoding
  // before React attaches onLoad, so check `complete` here or the skeleton would stick.
  useEffect(() => {
    setFailed(false);
    setPreviewFailed(false);
    const node = imgRef.current;
    setLoaded(!!node && node.complete && node.naturalWidth > 0);
  }, [src]);

  useEffect(() => clearTimers, []);

  const close = () => {
    clearTimers();
    setShown(false);
    closeTimer.current = setTimeout(() => setAnchor(null), PREVIEW_FADE);
  };

  // A stale card would float away from its row, so drop it as soon as anything scrolls.
  useEffect(() => {
    if (!anchor) return undefined;
    const onScroll = () => close();
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [anchor]);

  const open = () => {
    if (!src || failed) return;
    clearTimers();
    openTimer.current = setTimeout(() => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      // Prefer the right of the thumbnail; flip left when that would run off-screen.
      const toLeft = window.innerWidth - r.right < PREVIEW_W + PREVIEW_GAP + 12;
      const left = toLeft
        ? Math.max(12, r.left - PREVIEW_W - PREVIEW_GAP)
        : r.right + PREVIEW_GAP;
      const top = Math.min(
        Math.max(12, r.top + r.height / 2 - PREVIEW_H / 2),
        Math.max(12, window.innerHeight - PREVIEW_H - 12)
      );
      setAnchor({ top, left, toLeft });
      // Let the card paint in its start state, then flip to shown so the transition runs.
      // A timer rather than requestAnimationFrame: rAF is throttled when the tab isn't
      // compositing, which would leave the card mounted at opacity 0.
      revealTimer.current = setTimeout(() => setShown(true), 20);
    }, PREVIEW_DELAY);
  };

  if (!src || failed) return <ThumbFallback />;

  return (
    <div
      className={styles.thumbWrap}
      ref={wrapRef}
      onMouseEnter={open}
      onMouseLeave={close}
    >
      {!loaded && <div className={styles.thumbSkeleton} aria-hidden="true" />}
      <img
        ref={imgRef}
        className={`${styles.hotelThumb} ${loaded ? styles.hotelThumbLoaded : ""}`}
        src={src}
        alt={alt || "Hotel"}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={() => { setFailed(true); close(); }}
      />
      {anchor &&
        createPortal(
          <div
            className={`${styles.previewCard} ${shown ? styles.previewCardShown : ""} ${
              anchor.toLeft ? styles.previewFromRight : ""
            }`}
            style={{ top: anchor.top, left: anchor.left, width: PREVIEW_W }}
          >
            {previewFailed ? (
              <div className={styles.previewFallback}>
                <ImageOff size={34} />
                <span>Image unavailable</span>
              </div>
            ) : (
              <div className={styles.previewImgWrap}>
                {/* Same URL as the thumbnail, so it is usually already cached and paints instantly. */}
                <img
                  className={styles.previewImg}
                  src={src}
                  alt={alt || "Hotel"}
                  onError={() => setPreviewFailed(true)}
                />
              </div>
            )}
            <div className={styles.previewName}>{alt}</div>
          </div>,
          document.body
        )}
    </div>
  );
};

// Guest-rating (TripAdvisor, /10) filter buckets. Values are sent verbatim as the API's
// `rating` param, so this list and the server's whitelist must stay in step.
const GUEST_RATING_OPTIONS = [
  { value: "9", label: "9.0+ Exceptional" },
  { value: "8", label: "8.0+ Excellent" },
  { value: "7", label: "7.0+ Very good" },
  { value: "6", label: "6.0+ Good" },
  { value: "below6", label: "Below 6.0" },
  { value: "rated", label: "Has a rating" },
  { value: "unrated", label: "Not rated" },
];

const guestRatingLabel = (value) =>
  GUEST_RATING_OPTIONS.find((o) => o.value === value)?.label || value;

// Country filter with real flags and full names. A native <select> cannot render an
// <img> inside an <option>, and emoji flags are not an option either — Windows ships no
// flag glyphs, so they degrade to bare letter pairs on the machines this admin runs on.
// Hence a custom listbox, using the same flagcdn helper as the Geo Data country screen.
const CountryFilterSelect = ({ countries, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  useEffect(() => { if (open && searchRef.current) searchRef.current.focus(); }, [open]);

  const selected = countries.find((c) => c.countryCode === value);
  // Supplier codes and ISO codes diverge for a few entries (UK→GB, C0→CW, SF→MF), and
  // flagcdn only knows ISO — so prefer isoCode and let the helper's overrides cover the rest.
  const flagFor = (c) => flagSrcForCode(c?.isoCode || c?.countryCode);

  const filtered = query.trim()
    ? countries.filter((c) => {
        const q = query.trim().toLowerCase();
        return (c.countryName || "").toLowerCase().includes(q) || c.countryCode.toLowerCase().includes(q);
      })
    : countries;

  const pick = (code) => { onChange(code); setOpen(false); setQuery(""); };

  return (
    <div className={styles.countrySelectWrap} ref={wrapRef}>
      <button
        type="button"
        className={styles.countryTrigger}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selected ? (
          <>
            <CountryFlag src={flagFor(selected)} />
            <span className={styles.countryTriggerLabel}>{selected.countryName || selected.countryCode}</span>
          </>
        ) : (
          <span className={styles.countryTriggerLabel}>All Countries</span>
        )}
        <ChevronDown size={15} className={styles.countryCaret} />
      </button>

      {open && (
        <div className={styles.countryPanel} role="listbox">
          <div className={styles.countrySearchRow}>
            <Search size={14} />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country…"
            />
          </div>

          <div className={styles.countryOptions}>
            <button
              type="button"
              role="option"
              aria-selected={value === "all"}
              className={`${styles.countryOption} ${value === "all" ? styles.countryOptionActive : ""}`}
              onClick={() => pick("all")}
            >
              <span className={styles.countryFlagBox}><Globe size={14} /></span>
              <span className={styles.countryOptionName}>All Countries</span>
            </button>

            {filtered.map((c) => (
              <button
                key={c.countryCode}
                type="button"
                role="option"
                aria-selected={value === c.countryCode}
                className={`${styles.countryOption} ${value === c.countryCode ? styles.countryOptionActive : ""}`}
                onClick={() => pick(c.countryCode)}
              >
                <CountryFlag src={flagFor(c)} />
                <span className={styles.countryOptionName}>{c.countryName || c.countryCode}</span>
                <span className={styles.countryOptionCode}>{c.countryCode}</span>
              </button>
            ))}

            {!filtered.length && <div className={styles.countryEmpty}>No country matches “{query}”</div>}
          </div>
        </div>
      )}
    </div>
  );
};

// Flag image with a neutral placeholder, so a country flagcdn does not carry (or a
// blocked request) leaves the row aligned instead of collapsing.
const CountryFlag = ({ src }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (!src || failed) {
    return <span className={styles.countryFlagBox}><Globe size={14} /></span>;
  }
  return (
    <img className={styles.countryFlagImg} src={src} alt="" loading="lazy" onError={() => setFailed(true)} />
  );
};

// Windowed page list: 1 … (c-1) c (c+1) … N — keeps the bar compact even at 13k+ pages.
const getPageItems = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const items = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev > 1) items.push(`gap-${prev}`);
    items.push(p);
    prev = p;
  }
  return items;
};

const HotelManagment = () => {
  const { showToast } = useToast();
  const searchInputRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(readPageSize);
  // A new page size keeps the search, filters and sort, and starts again at page 1.
  const changeLimit = (n) => {
    if (!PAGE_SIZE_OPTIONS.includes(n)) return;
    try { window.localStorage.setItem(PAGE_SIZE_KEY, String(n)); } catch { /* blocked storage: this visit only */ }
    setLimit(n);
    setPage(1);
  };
  const [statusFilter, setStatusFilter] = useState("all");
  const [countryFilter, setCountryFilter] = useState("all");
  // Geo chain: country → city (destination) → zone. Both hold geo CODES, not display names.
  const [cityFilter, setCityFilter] = useState("all");
  const [zoneFilter, setZoneFilter] = useState("all");
  const [ratingFilter, setRatingFilter] = useState("all");        // star category (1–5★)
  const [guestRatingFilter, setGuestRatingFilter] = useState("all"); // TripAdvisor /10 score
  const [showAddModal, setShowAddModal] = useState(false);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [zones, setZones] = useState([]);
  const [addHotelLoading, setAddHotelLoading] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  // Export UI: the two-option menu, and the in-progress "all pages" download.
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportDone, setExportDone] = useState(0);   // rows fetched so far (for the progress label)
  const downloadRef = useRef(null);

  // Rating switch-off: preview (counts) → confirm → apply.
  const [ratingModal, setRatingModal] = useState(null);   // null | { loading } | preview data
  const [ratingApplying, setRatingApplying] = useState(false);

  const openRatingRule = async () => {
    setRatingModal({ loading: true });
    try {
      const res = await getRatingSwitchOffPreview();
      if (res?.success) setRatingModal(res.data);
      else { setRatingModal(null); showToast(res?.message || "Could not load the preview", "error"); }
    } catch (err) {
      setRatingModal(null);
      showToast(err.response?.data?.message || "Could not load the preview", "error");
    }
  };

  const confirmRatingRule = async () => {
    setRatingApplying(true);
    try {
      const res = await applyRatingSwitchOff({});
      showToast(res?.message || "Rating rule applied", "success");
      setRatingModal(null);
      refetch();
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to apply the rating rule", "error");
    } finally {
      setRatingApplying(false);
    }
  };
  const updateSearchQuery = useDebouncedCallback((value) => {
    setSearchQuery(value.trim());
    setPage(1);
  }, 350);

  // The API speaks the same vocabulary the dropdown offers (star:N / key:N / stars / keys /
  // none), so there is nothing to translate. It used to map 1–5 onto the literal codes
  // 1EST..5EST, which is why apartments and unclassified hotels could not be filtered at all
  // and "5 Star" quietly missed every hotel coded 5LUX.
  const categoryFilter = ratingFilter;

  const {
    data: hotels,
    pagination,
    loading,
    error,
    refetch,
  } = useGetHotels({
    page,
    limit,
    search: searchQuery,
    status: statusFilter,
    country: countryFilter,
    city: cityFilter,
    zone: zoneFilter,
    category: categoryFilter,
    rating: guestRatingFilter,
  });

  const loadCountries = async (force = false) => {
    try {
      const res = await getHotelCountries({ force });
      if (res.success) {
        setCountries(res.data || []);
      }
    } catch (err) {
      console.error("Failed to load hotel countries", err);
    }
  };

  const loadCities = async (countryCode = "") => {
    if (!countryCode || countryCode === "all") {
      setCities([]);
      return;
    }

    try {
      const res = await getHotelCities({ country: countryCode });
      if (res.success) {
        setCities(res.data || []);
      }
    } catch (err) {
      console.error("Failed to load hotel cities", err);
      setCities([]);
    }
  };

  // Zones belong to one city, so there is nothing to offer until a city is chosen.
  const loadZones = async (countryCode = "", cityCode = "") => {
    if (!cityCode || cityCode === "all") {
      setZones([]);
      return;
    }

    try {
      const res = await getHotelZones({ country: countryCode, city: cityCode });
      if (res.success) {
        setZones(res.data || []);
      }
    } catch (err) {
      console.error("Failed to load hotel zones", err);
      setZones([]);
    }
  };

  useEffect(() => {
    loadCountries();
  }, []);

  // Close the export menu on an outside click / Escape.
  useEffect(() => {
    if (!downloadMenuOpen) return undefined;
    const onDown = (e) => { if (downloadRef.current && !downloadRef.current.contains(e.target)) setDownloadMenuOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setDownloadMenuOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [downloadMenuOpen]);

  useEffect(() => {
    loadCities(countryFilter);
  }, [countryFilter]);

  useEffect(() => {
    loadZones(countryFilter, cityFilter);
  }, [countryFilter, cityFilter]);

  // Each level resets the ones below it — a zone from the previous city would filter to zero.
  const handleCountryChange = (value) => {
    setCountryFilter(value);
    setCityFilter("all");
    setZoneFilter("all");
    setPage(1);
  };

  const handleCityChange = (value) => {
    setCityFilter(value);
    setZoneFilter("all");
    setPage(1);
  };

  const hotelDetailsPath = (hotelId) => `/products/hotels/details/${hotelId}`;

  const handleSaveHotel = async (hotelData) => {
    setAddHotelLoading(true);
    try {
      const payload = mapUiCreateHotelToApi(hotelData);
      const res = await createHotel(payload);
      if (!res.success) {
        showToast(res.message || "Failed to add hotel", "error");
        return false;
      }

      // Upload any selected images now that the hotel record (and its id) exists.
      const newId = res.data?.id;
      const images = Array.isArray(hotelData.images) ? hotelData.images : [];
      if (newId && images.length) {
        const results = await Promise.allSettled(images.map((file) => uploadHotelImage(newId, file)));
        const failed = results.filter((r) => r.status === "rejected" || r.value?.success === false).length;
        if (failed) {
          showToast(`Hotel saved, but ${failed} of ${images.length} image(s) failed to upload. You can add them later from the hotel page.`, "error");
        }
      }

      showToast("Hotel added successfully", "success");
      setShowAddModal(false);
      setPage(1);
      refetch();
      loadCountries(true); // a new hotel may introduce a new country → bust the cache
      return true;
    } catch (err) {
      const message = err.response?.data?.message || err.message || "Failed to add hotel";
      showToast(message, "error");
      return false;
    } finally {
      setAddHotelLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
  };

  const handleRefresh = () => {
    refetch();
  };

  // "Refresh content" — pull the latest Hotelbeds content for the hotels currently in scope, but
  // ONLY for the field groups the operator ticks in the dialog. Everything else (and always the
  // on/off status, reviews, uploaded/master photos, zone override) is left exactly as they set
  // it. Some scope is always required so a mis-click can't refresh the whole catalogue:
  //   • a geo filter (country / city / zone) — the backend resolves and batches those itself;
  //   • otherwise whatever the remaining filters narrow the list to, up to REFRESH_FILTER_MAX
  //     hotels, sent as an explicit list of hotel codes. That is what makes "search one hotel,
  //     refresh just that hotel" work.
  const [refreshingContent, setRefreshingContent] = useState(false);
  const [refreshModalOpen, setRefreshModalOpen] = useState(false);
  const [refreshFields, setRefreshFields] = useState({
    basic: true, description: true, facilities: true,      // pure Hotelbeds content — safe defaults
    images: false, zone: false, location: false, geo: false, // often hand-curated — off by default
  });
  const geoScope = zoneFilter !== "all"
    ? { zoneCode: zoneFilter }
    : cityFilter !== "all"
      ? { destinationCode: cityFilter }
      : countryFilter !== "all"
        ? { countryCode: countryFilter }
        : null;
  // Filters that narrow the list without naming a place. On their own they still define a
  // scope — the hotels the table is showing — as long as there aren't too many of them.
  const hasNarrowingFilter =
    !!searchQuery || statusFilter !== "all" || ratingFilter !== "all" || guestRatingFilter !== "all";
  const narrowedTotal = pagination.total || 0;
  const filterScopeActive =
    !geoScope &&
    hasNarrowingFilter &&
    !loading &&            // a stale total from the previous filter must not open the button
    narrowedTotal > 0 &&
    narrowedTotal <= REFRESH_FILTER_MAX;
  const contentScope = geoScope || (filterScopeActive ? { filtered: true } : null);

  const contentScopeLabel = zoneFilter !== "all"
    ? (zones.find((z) => String(z.zoneCode) === String(zoneFilter))?.zoneName || `Zone ${zoneFilter}`)
    : cityFilter !== "all"
      ? (cities.find((c) => c.cityCode === cityFilter)?.cityName || cityFilter)
      : countryFilter !== "all"
        ? countryFilter
        // A single match is almost always a hotel searched by name — say which one, so the
        // dialog confirms the operator is refreshing the hotel they think they are.
        : filterScopeActive
          ? (narrowedTotal === 1
              ? (hotels[0]?.hotelName || "1 hotel matching this filter")
              : `${narrowedTotal} hotels matching this filter`)
          : "—";
  const anyRefreshField = Object.values(refreshFields).some(Boolean);

  // Why the button is off, in the operator's own terms.
  const refreshDisabledReason =
    hasNarrowingFilter && narrowedTotal > REFRESH_FILTER_MAX
      ? `${narrowedTotal.toLocaleString()} hotels match — narrow to ${REFRESH_FILTER_MAX} or fewer, or pick a country / city / zone`
      : "Search a hotel, or select a country, city, or zone first";

  // The hotel codes a filter-scoped refresh applies to. Resolved from the same API the table
  // reads, with the same filters, so it is exactly the list on screen — including the rows on
  // pages the operator never opened.
  const resolveFilteredHotelCodes = async () => {
    const res = await getHotels({ ...buildExportParams(), page: 1, limit: REFRESH_FILTER_MAX });
    if (!res?.success) throw new Error(res?.message || "Could not read the filtered hotels.");
    return (res.data || []).map((h) => h.hotelCode).filter(Boolean);
  };

  const confirmRefreshContent = async () => {
    if (!contentScope || refreshingContent || !anyRefreshField) return;
    setRefreshingContent(true);
    try {
      let scope = geoScope;
      if (!scope) {
        const hotelCodes = await resolveFilteredHotelCodes();
        if (!hotelCodes.length) {
          showToast("None of the filtered hotels carry a Hotelbeds code, so there is nothing to refresh.", "error");
          return;
        }
        scope = { hotelCodes };
      }
      const res = await refreshHotelContent({ ...scope, fields: refreshFields });
      showToast(res?.message || "Content refreshed.", "success");
      setRefreshModalOpen(false);
      refetch();
    } catch (err) {
      showToast(
        err?.response?.data?.message || err?.message || "Failed to refresh content.",
        "error"
      );
    } finally {
      setRefreshingContent(false);
    }
  };

  // The active filters as API params — the SAME set useGetHotels sends, so an export always
  // matches exactly what the table is showing (search + status + geo + rating).
  const buildExportParams = () => {
    const p = { sortBy: "name", sortOrder: "ASC" };
    if (searchQuery) p.search = searchQuery;
    if (statusFilter !== "all") p.status = statusFilter;
    if (countryFilter !== "all") p.country = countryFilter;
    if (cityFilter !== "all") p.city = cityFilter;
    if (zoneFilter !== "all") p.zone = zoneFilter;
    if (categoryFilter !== "all") p.category = categoryFilter;
    if (guestRatingFilter !== "all") p.rating = guestRatingFilter;
    return p;
  };

  // Human-readable one-liner of the active filters, printed in the PDF header so a shared report
  // states what it's a report OF. Only readable values (codes/text) — not opaque city ids.
  const buildFilterSummary = () => {
    const parts = [];
    if (searchQuery) parts.push(`"${searchQuery}"`);
    if (countryFilter !== "all") parts.push(countryFilter);
    // City and zone narrow the export too, so name them — by their geo label, not the code
    // the request carries.
    if (cityFilter !== "all") {
      parts.push(cities.find((c) => c.cityCode === cityFilter)?.cityName || cityFilter);
    }
    if (zoneFilter !== "all") {
      parts.push(zones.find((z) => String(z.zoneCode) === String(zoneFilter))?.zoneName || zoneFilter);
    }
    if (ratingFilter !== "all") parts.push(categoryFilterLabel(ratingFilter));
    if (guestRatingFilter !== "all") parts.push(`Guest ${guestRatingLabel(guestRatingFilter)}`);
    if (statusFilter !== "all") parts.push(statusFilter === "active" ? "Active" : "Inactive");
    return parts.join(" · ");
  };

  // Hand a set of rows to the chosen format. One place so CSV and PDF can never carry different
  // data or naming.
  const deliver = (rows, format, scopeLabel) => {
    if (format === "pdf") {
      printHtml(
        hotelsToPrintableHtml(rows, {
          title: "Hotels Export",
          subtitle: scopeLabel,
          filterSummary: buildFilterSummary(),
          generatedAt: new Date(),
        })
      );
    } else {
      const stamp = scopeLabel.includes("page") ? `page-${page}` : `all-${rows.length}`;
      downloadCsv(hotelsToCsv(rows), `hotels-${stamp}.csv`);
    }
  };

  // "This page" — the rows already on screen, no extra request.
  const exportCurrentPage = (format) => {
    setDownloadMenuOpen(false);
    if (!hotels.length) { showToast("No hotels to export", "info"); return; }
    deliver(hotels, format, `This page (page ${page})`);
  };

  // "All pages" — every row matching the current filters, fetched in batches. Sequential (not
  // parallel) so a large export can't fan out into a burst that overloads the DB, and so the
  // progress count is honest. Guarded by EXPORT_MAX so an unfiltered table asks for a filter
  // first rather than firing ~140 requests.
  const exportAllPages = async (format) => {
    if (exporting) return;
    setDownloadMenuOpen(false);
    setExporting(true);
    setExportDone(0);
    try {
      const base = buildExportParams();

      // First batch also tells us the total, so we know how many pages to walk.
      const first = await getHotels({ ...base, page: 1, limit: EXPORT_BATCH });
      if (!first?.success) throw new Error(first?.message || "Export failed");

      const total = first.pagination?.total ?? (first.data || []).length;
      if (!total) { showToast("No hotels to export", "info"); return; }
      if (total > EXPORT_MAX) {
        showToast(
          `That's ${total.toLocaleString()} hotels — too many to export at once. Narrow it with a filter (country, city, status…) and try again.`,
          "error"
        );
        return;
      }

      let rows = (first.data || []).map(mapApiHotelToUi);
      setExportDone(rows.length);

      const totalPages = Math.ceil(total / EXPORT_BATCH);
      for (let pg = 2; pg <= totalPages; pg++) {
        const res = await getHotels({ ...base, page: pg, limit: EXPORT_BATCH });
        if (res?.success) {
          rows = rows.concat((res.data || []).map(mapApiHotelToUi));
          setExportDone(rows.length);
        }
      }

      deliver(rows, format, `All pages (${rows.length.toLocaleString()} hotels)`);
      showToast(`Exported ${rows.length.toLocaleString()} hotels`, "success");
    } catch (err) {
      showToast(err?.response?.data?.message || err?.message || "Export failed", "error");
    } finally {
      setExporting(false);
      setExportDone(0);
    }
  };

  const handleClearFilters = () => {
    setStatusFilter("all");
    setCountryFilter("all");
    setCityFilter("all");
    setZoneFilter("all");
    setRatingFilter("all");
    setGuestRatingFilter("all");
    setSearchQuery("");
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    setPage(1);
  };

  const activeFiltersCount =
    (searchQuery ? 1 : 0) +
    (statusFilter !== "all" ? 1 : 0) +
    (countryFilter !== "all" ? 1 : 0) +
    (cityFilter !== "all" ? 1 : 0) +
    (zoneFilter !== "all" ? 1 : 0) +
    (ratingFilter !== "all" ? 1 : 0) +
    (guestRatingFilter !== "all" ? 1 : 0);

  const handleToggleStatus = async (hotelId, currentStatus) => {
    setToggleLoading(true);
    try {
      const targetActive = currentStatus !== "active";
      const res = await toggleHotelStatus(hotelId, targetActive);
      if (!res.success) {
        showToast(res.message || "Failed to toggle hotel status", "error");
        return;
      }
      showToast(`Hotel ${targetActive ? "activated" : "deactivated"}`, "success");
      refetch();
    } catch (err) {
      const message = err.response?.data?.message || err.message || "Failed to toggle hotel status";
      showToast(message, "error");
    } finally {
      setToggleLoading(false);
    }
  };

  // CATEGORY = the property's official classification, which is not always stars. Hotelbeds
  // rates hotels in stars and apartments in KEYS (llaves: 3LL, APTH4), and leaves a large
  // slice of the catalogue — guesthouses, boutiques, villas, B&Bs — classified with neither.
  // Five empty stars said "zero stars" for all of those, which is itself a rating; they simply
  // do not have one, so they get the no-category badge instead.
  const renderCategory = ({ ratingKind, ratingValue }) => {
    if (!ratingKind || !ratingValue) {
      return (
        <span className={styles.noCategory} title="No official star or key category">
          <NoCategoryIcon />
        </span>
      );
    }

    const Symbol = ratingKind === "key" ? KeyRound : Star;
    const label = `${ratingValue} ${ratingKind === "key" ? "key" : "star"}${ratingValue > 1 ? "s" : ""}`;

    return (
      <span className={styles.categorySymbols} title={label}>
        {[...Array(5)].map((_, index) => {
          const earned = index < ratingValue;
          return (
            <Symbol
              key={index}
              size={14}
              className={earned ? styles.starFilled : styles.starEmpty}
              // Keys read as a solid blob when filled, so only stars take a fill.
              fill={earned && ratingKind === "star" ? "#f59e0b" : "none"}
            />
          );
        })}
      </span>
    );
  };

  const totalPages = Math.max(pagination.totalPages || 1, 1);
  const currentPage = pagination.page || page;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1>Master Hotels</h1>
          <p className={styles.subtitle}>
            Manage hotel master database for bookings, mappings, and inventory management. Add hotels with contact details and ratings.
          </p>
        </div>
        <button className={styles.addBtn} onClick={() => setShowAddModal(true)} disabled={addHotelLoading}>
          + Add Hotel
        </button>
      </div>

      <div className={styles.infoBanner}>
        <div className={styles.infoIcon}>ℹ️</div>
        <div className={styles.infoText}>
          <strong>Master Data Notice:</strong> Hotels are the foundation for room mapping and inventory management. Each hotel must have complete contact information and accurate star rating.
        </div>
      </div>

      <div className={styles.controls}>
        <div className={styles.searchBox}>
          <Search size={18} className={styles.searchIcon} />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search by hotel name, city, or country..."
            defaultValue=""
            onChange={(e) => updateSearchQuery(e.target.value)}
          />
        </div>

        <div className={styles.filters}>
          <CountryFilterSelect
            countries={countries}
            value={countryFilter}
            onChange={handleCountryChange}
          />

          {/* City then Zone, both straight from Geo Data. Values are geo codes; the labels
              are the curated geo names, so no supplier spelling reaches this bar. */}
          <div className={styles.selectWrapper}>
            <select
              value={cityFilter}
              onChange={(e) => handleCityChange(e.target.value)}
              className={`${styles.filterSelect} ${countryFilter === "all" ? styles.filterSelectDisabled : ""}`}
              disabled={countryFilter === "all"}
            >
              <option value="all">All Cities</option>
              {cities.map((city) => (
                <option key={city.cityCode} value={city.cityCode}>
                  {city.cityName}
                </option>
              ))}
            </select>
            {countryFilter === "all" && (
              <span className={styles.disabledHint}>Select country first</span>
            )}
          </div>

          <div className={styles.selectWrapper}>
            <select
              value={zoneFilter}
              onChange={(e) => {
                setZoneFilter(e.target.value);
                setPage(1);
              }}
              className={`${styles.filterSelect} ${cityFilter === "all" ? styles.filterSelectDisabled : ""}`}
              disabled={cityFilter === "all"}
            >
              <option value="all">All Zones</option>
              {zones.map((zone) => (
                <option key={zone.zoneCode} value={zone.zoneCode}>
                  {zone.zoneName}
                </option>
              ))}
            </select>
            {cityFilter === "all" && (
              <span className={styles.disabledHint}>Select city first</span>
            )}
          </div>

          <select
            value={ratingFilter}
            onChange={(e) => {
              setRatingFilter(e.target.value);
              setPage(1);
            }}
            className={styles.filterSelect}
          >
            <option value="all">All Classes</option>
            <optgroup label="Stars — hotels">
              <option value="stars">Any star hotel</option>
              {[5, 4, 3, 2, 1].map((n) => (
                <option key={`star:${n}`} value={`star:${n}`}>{n} Star</option>
              ))}
            </optgroup>
            <optgroup label="Keys — apartments">
              <option value="keys">Any key apartment</option>
              {[5, 4, 3, 2, 1].map((n) => (
                <option key={`key:${n}`} value={`key:${n}`}>{n}-key apartment</option>
              ))}
            </optgroup>
            <optgroup label="Unclassified">
              <option value="none">Without official class</option>
            </optgroup>
          </select>

          <select
            value={guestRatingFilter}
            onChange={(e) => {
              setGuestRatingFilter(e.target.value);
              setPage(1);
            }}
            className={styles.filterSelect}
            title="Filter by stored TripAdvisor guest rating (/10)"
          >
            <option value="all">All Guest Ratings</option>
            {GUEST_RATING_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className={styles.filterSelect}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          {activeFiltersCount > 0 && (
            <button className={styles.clearFiltersBtn} onClick={handleClearFilters}>
              <Filter size={16} />
              Clear ({activeFiltersCount})
            </button>
          )}

          <button className={styles.iconBtn} onClick={handleRefresh} title="Refresh">
            <RefreshCw size={18} />
          </button>

          <button
            className={styles.ratingRuleBtn}
            onClick={() => setRefreshModalOpen(true)}
            disabled={!contentScope || refreshingContent}
            title={contentScope
              ? `Pull the latest Hotelbeds content for ${contentScopeLabel}`
              : refreshDisabledReason}
          >
            {refreshingContent
              ? <RefreshCw size={15} className={styles.spin} />
              : <Layers size={15} />}
            {refreshingContent ? "Refreshing…" : "Refresh content"}
          </button>

          <button
            className={styles.ratingRuleBtn}
            onClick={openRatingRule}
            title="Switch off hotels with low guest ratings"
          >
            <Star size={15} />
            Apply rating rule
          </button>

          <div className={styles.downloadWrap} ref={downloadRef}>
            <button
              className={styles.iconBtn}
              onClick={() => setDownloadMenuOpen((o) => !o)}
              title="Export"
              disabled={exporting}
              aria-haspopup="menu"
              aria-expanded={downloadMenuOpen}
            >
              {exporting ? <RefreshCw size={18} className={styles.spin} /> : <Download size={18} />}
              <ChevronDown size={13} className={styles.iconBtnCaret} />
            </button>

            {exporting && (
              <span className={styles.exportProgress}>
                Exporting… {exportDone.toLocaleString()}
                {pagination.total ? ` / ${pagination.total.toLocaleString()}` : ""}
              </span>
            )}

            {downloadMenuOpen && !exporting && (
              <div className={styles.downloadMenu} role="menu">
                <div className={styles.downloadScope}>
                  <div className={styles.downloadScopeLabel}>
                    <FileText size={15} />
                    <span className={styles.downloadItemText}>
                      <span className={styles.downloadItemTitle}>This page</span>
                      <span className={styles.downloadItemSub}>{hotels.length} hotel{hotels.length === 1 ? "" : "s"}</span>
                    </span>
                  </div>
                  <div className={styles.downloadFormats}>
                    <button className={styles.formatBtn} onClick={() => exportCurrentPage("csv")} disabled={!hotels.length}>CSV</button>
                    <button className={styles.formatBtn} onClick={() => exportCurrentPage("pdf")} disabled={!hotels.length}>PDF</button>
                  </div>
                </div>

                <div className={styles.downloadScope}>
                  <div className={styles.downloadScopeLabel}>
                    <Layers size={15} />
                    <span className={styles.downloadItemText}>
                      <span className={styles.downloadItemTitle}>All pages{activeFiltersCount > 0 ? " (filtered)" : ""}</span>
                      <span className={styles.downloadItemSub}>{(pagination.total || 0).toLocaleString()} hotel{pagination.total === 1 ? "" : "s"}</span>
                    </span>
                  </div>
                  <div className={styles.downloadFormats}>
                    <button className={styles.formatBtn} onClick={() => exportAllPages("csv")} disabled={!pagination.total}>CSV</button>
                    <button className={styles.formatBtn} onClick={() => exportAllPages("pdf")} disabled={!pagination.total}>PDF</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.resultsCount}>
        Showing {hotels.length} of {pagination.total || 0} hotels
        {activeFiltersCount > 0 && <span className={styles.filterIndicator}> (filtered)</span>}
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.thHotel}>HOTEL</th>
              <th>LOCATION</th>
              <th>ZONE</th>
              <th>CATEGORY</th>
              <th>CONTACT INFO</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className={styles.noData}>
                  <div className={styles.noDataContent}>
                    <p>Loading hotels...</p>
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="7" className={styles.noData}>
                  <div className={styles.noDataContent}>
                    <p>Failed to load hotels</p>
                    <span>{error}</span>
                  </div>
                </td>
              </tr>
            ) : hotels.length === 0 ? (
              <tr>
                <td colSpan="7" className={styles.noData}>
                  <div className={styles.noDataContent}>
                    <div className={styles.noDataIcon}>🏨</div>
                    <p>No hotels found</p>
                    <span>Try adjusting your search or filters</span>
                  </div>
                </td>
              </tr>
            ) : (
              hotels.map((hotel) => (
                <tr key={hotel.id}>
                  <td>
                    <div className={styles.hotelNameCell}>
                      <HotelThumb src={hotel.primaryImage} alt={hotel.hotelName} />
                      <div className={styles.hotelNameInfo}>
                        <strong>{hotel.hotelName}</strong>
                        {hotel.hotelCode && (
                          <span className={styles.hotelCode}>#{hotel.hotelCode}</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className={styles.locationCell}>
                      <MapPin size={14} className={styles.locationIcon} />
                      {/* City only — zone has its own column, and the country is already the
                          filter you arrived through, so both just crowded the cell. */}
                      <span className={styles.city}>{hotel.city || "—"}</span>
                    </div>
                  </td>
                  <td>
                    {hotel.zone ? (
                      <span className={styles.zonePill} title={hotel.zone}>
                        <Map size={12} />
                        {hotel.zone}
                      </span>
                    ) : (
                      <span className={styles.zoneEmpty}>—</span>
                    )}
                  </td>
                  <td>
                    <div className={styles.ratingCell}>
                      {renderCategory(hotel)}
                      {hotel.guestRating && (
                        <span
                          className={styles.guestRating}
                          title={`TripAdvisor · ${hotel.guestRating.count.toLocaleString()} reviews`}
                        >
                          {hotel.guestRating.score10.toFixed(1)}/10
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className={styles.contactInfo}>
                      <div className={styles.contactRow}>
                        <Phone size={12} />
                        <span>{hotel.contactPhone || "-"}</span>
                      </div>
                      <div className={styles.contactRow}>
                        <Mail size={12} />
                        <span>{hotel.contactEmail || "-"}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className={styles.toggleCell}>
                      <button
                        className={`${styles.toggle} ${hotel.status === "active" ? styles.toggleOn : styles.toggleOff}`}
                        onClick={() => handleToggleStatus(hotel.id, hotel.status)}
                        disabled={toggleLoading || addHotelLoading}
                        title={hotel.status === "active" ? "Click to deactivate" : "Click to activate"}
                        aria-label={`Toggle status for ${hotel.hotelName}`}
                      >
                        <span className={styles.toggleThumb} />
                      </button>
                      <span className={`${styles.toggleLabel} ${hotel.status === "active" ? styles.toggleLabelOn : styles.toggleLabelOff}`}>
                        {hotel.status === "active" ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      {/* A real link, not a button with window.open: the browser then gives
                          middle-click, ctrl/cmd-click and "open in new tab" for free, and the
                          row keeps its place in the list behind the new tab. */}
                      <a
                        className={styles.openBtn}
                        href={hotelDetailsPath(hotel.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open hotel details in a new tab"
                      >
                        <ExternalLink size={16} />
                        <span>Open</span>
                      </a>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.paginationBar}>
        <div className={styles.paginationLeft}>
          <span>
            Showing{" "}
            <strong className={styles.rangeStrong}>
              {pagination.total ? ((currentPage - 1) * limit + 1).toLocaleString() : 0}
              {"–"}
              {Math.min(currentPage * limit, pagination.total || 0).toLocaleString()}
            </strong>{" "}
            of <strong className={styles.rangeStrong}>{(pagination.total || 0).toLocaleString()}</strong> hotels
          </span>
          <label className={styles.perPageChip}>
            <select
              aria-label="Hotels per page"
              value={limit}
              onChange={(e) => changeLimit(Number(e.target.value))}
              className={styles.perPageSelect}
            >
              {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            {" "}/ page
          </label>
        </div>

        <div className={styles.paginationRight}>
          <button
            className={styles.pageNavBtn}
            onClick={() => setPage(1)}
            disabled={currentPage <= 1 || loading}
            title="First page"
            aria-label="First page"
          >
            <ChevronsLeft size={16} />
          </button>
          <button
            className={styles.pageNavBtn}
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage <= 1 || loading}
            title="Previous page"
            aria-label="Previous page"
          >
            <ChevronLeft size={16} />
          </button>

          {getPageItems(currentPage, totalPages).map((item) =>
            typeof item === "number" ? (
              <button
                key={item}
                className={`${styles.pageBtn} ${item === currentPage ? styles.pageBtnActive : ""}`}
                onClick={() => setPage(item)}
                disabled={loading || item === currentPage}
                aria-current={item === currentPage ? "page" : undefined}
              >
                {item.toLocaleString()}
              </button>
            ) : (
              <span key={item} className={styles.pageEllipsis}>
                …
              </span>
            )
          )}

          <button
            className={styles.pageNavBtn}
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage >= totalPages || loading}
            title="Next page"
            aria-label="Next page"
          >
            <ChevronRight size={16} />
          </button>
          <button
            className={styles.pageNavBtn}
            onClick={() => setPage(totalPages)}
            disabled={currentPage >= totalPages || loading}
            title="Last page"
            aria-label="Last page"
          >
            <ChevronsRight size={16} />
          </button>
        </div>
      </div>

      {showAddModal && (
        <AddHotelModal
          open={showAddModal}
          onClose={handleCloseModal}
          onSave={handleSaveHotel}
          hotel={null}
          saving={addHotelLoading}
        />
      )}

      {ratingModal && (
        <div className={styles.ratingOverlay} onClick={() => !ratingApplying && setRatingModal(null)}>
          <div className={styles.ratingDialog} onClick={(e) => e.stopPropagation()}>
            {ratingModal.loading ? (
              <div className={styles.ratingLoading}><RefreshCw size={18} className={styles.spin} /> Checking ratings…</div>
            ) : (
              <>
                <h3 className={styles.ratingTitle}>Apply guest-rating rule</h3>
                <p className={styles.ratingSub}>
                  Based on stored TripAdvisor ratings (hotels with fewer than {ratingModal.minReviews} reviews are ignored):
                </p>
                <div className={styles.ratingRows}>
                  <div className={styles.ratingRow}>
                    <span className={styles.ratingBig}>{Number(ratingModal.disableCount).toLocaleString()}</span>
                    <span>hotels below <b>{ratingModal.disableBelow}/10</b> will be <b>switched off</b>.</span>
                  </div>
                  <div className={styles.ratingRow}>
                    <span className={styles.ratingBig}>{Number(ratingModal.reviewCount).toLocaleString()}</span>
                    <span>hotels <b>{ratingModal.disableBelow}–{ratingModal.reviewBelow}/10</b> will be <b>flagged for manual review</b> (stay on).</span>
                  </div>
                </div>
                <div className={styles.ratingActions}>
                  <button className={styles.ratingCancel} onClick={() => setRatingModal(null)} disabled={ratingApplying}>Cancel</button>
                  <button className={styles.ratingConfirm} onClick={confirmRatingRule} disabled={ratingApplying || ratingModal.disableCount + ratingModal.reviewCount === 0}>
                    {ratingApplying ? "Applying…" : `Switch off ${Number(ratingModal.disableCount).toLocaleString()} hotels`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {refreshModalOpen && (
        <div className={styles.ratingOverlay} onClick={() => !refreshingContent && setRefreshModalOpen(false)}>
          <div className={styles.ratingDialog} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.ratingTitle}>Refresh content from Hotelbeds</h3>
            <p className={styles.ratingSub}>
              Scope: <b>{contentScopeLabel}</b>. Tick only what you want overwritten with the latest
              Hotelbeds data — anything you leave unticked keeps your current values.
            </p>
            <div className={styles.refreshOptions}>
              {REFRESH_OPTIONS.map((o) => (
                <label key={o.key} className={styles.refreshOption}>
                  <input
                    type="checkbox"
                    checked={!!refreshFields[o.key]}
                    onChange={(e) => setRefreshFields((p) => ({ ...p, [o.key]: e.target.checked }))}
                    disabled={refreshingContent}
                  />
                  <span className={styles.refreshOptText}>
                    <span className={styles.refreshOptLabel}>{o.label}</span>
                    <span className={styles.refreshOptHint}>{o.hint}</span>
                  </span>
                </label>
              ))}
            </div>
            <p className={styles.refreshNote}>
              Always kept safe: <b>active / inactive</b> status, <b>guest reviews</b>, and your{" "}
              <b>uploaded &amp; master photos</b> — a refresh never changes these.
            </p>
            <div className={styles.ratingActions}>
              <button className={styles.ratingCancel} onClick={() => setRefreshModalOpen(false)} disabled={refreshingContent}>Cancel</button>
              <button className={styles.ratingConfirm} onClick={confirmRefreshContent} disabled={refreshingContent || !anyRefreshField}>
                {refreshingContent ? "Refreshing…" : "Refresh selected"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HotelManagment;
